import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Badge,
} from "@/components/primitives";
import { Target } from "lucide-react";
import { StatusIcon } from "./StatusIcon";
import { getStatusBadgeColor, getStatusLabel } from "../utils";
import type { CorrectiveAction } from "../types";

interface CorrectiveActionsCardProps {
	actions: CorrectiveAction[];
}

export function CorrectiveActionsCard({ actions }: CorrectiveActionsCardProps) {
	if (actions.length === 0) {
		return null;
	}

	return (
		<Card className="border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm print-borderless">
			<CardHeader className="pb-4 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
				<div className="flex items-center gap-3">
					<div className="p-2 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600">
						<Target className="h-5 w-5 text-white" />
					</div>
					<div>
						<CardTitle className="text-xl sm:text-2xl font-semibold">
							Corrective Actions{" "}
							<span className="print-hide-count">({actions.length})</span>
						</CardTitle>
					</div>
				</div>
			</CardHeader>
			<CardContent className="pt-6">
				<div className="space-y-4">
					{actions.map((action) => (
						<div
							key={action.id}
							className="border-2 rounded-lg p-4 bg-green-50/50 dark:bg-green-900/10 border-green-200 dark:border-green-800 shadow-md"
						>
							<div className="flex items-start justify-between">
								<div className="flex-1">
									<div className="flex items-center space-x-2 mb-2">
										<h4 className="font-semibold text-foreground">
											{action.title}
										</h4>
										<Badge
											variant="outline"
											className={`${getStatusBadgeColor(action.status)} print-hide-status`}
										>
											<span className="flex items-center space-x-1">
												<StatusIcon status={action.status} />
												<span className="capitalize">
													{getStatusLabel(action.status)}
												</span>
											</span>
										</Badge>
									</div>
									<p className="text-sm text-muted-foreground leading-relaxed">
										{action.description}
									</p>
								</div>
								<div className="text-right ml-4 print-hide-priority">
									<span className="text-xs font-semibold text-muted-foreground">
										Priority: {action.priority}
									</span>
								</div>
							</div>
						</div>
					))}
				</div>
			</CardContent>
		</Card>
	);
}
